import React from 'react';
import { motion } from 'framer-motion';
import { CheckSquareIcon } from 'lucide-react';
interface IntroSectionProps {
  onBegin: () => void;
}
export function IntroSection({
  onBegin
}: IntroSectionProps) {
  const checklistItems = ['Does the narrative flow make sense for your audience?', 'Is the FABP backbone (Mechanism Differentiation) the right strategic angle?', 'Are the interactive elements (polls, quiz, ranking) appropriate?', 'Does the visual direction match your brand guidelines?', 'Are there any claims that need to be flagged for MLR?', 'Is anything missing from the story?'];
  return <section className="min-h-screen bg-gray-50" data-id="element-1">
      {/* Header */}
      <div className="bg-[#0d1b2a] text-white py-8 px-6" data-id="element-2">
        <div className="max-w-md mx-auto" data-id="element-3">
          <div className="border-t-2 border-b-2 border-gray-600 py-4 mb-6" data-id="element-4">
            <h1 className="text-center text-sm font-bold tracking-widest uppercase" data-id="element-5">
              Storyboard Review — Confidential
            </h1>
          </div>

          <div className="space-y-2 text-sm" data-id="element-6">
            <div className="flex justify-between" data-id="element-7">
              <span className="text-gray-400" data-id="element-8">Company:</span>
              <span className="font-semibold" data-id="element-9">Roche</span>
            </div>
            <div className="flex justify-between" data-id="element-10">
              <span className="text-gray-400" data-id="element-11">Country:</span>
              <span className="font-semibold" data-id="element-12">Ivory Coast</span>
            </div>
            <div className="flex justify-between" data-id="element-13">
              <span className="text-gray-400" data-id="element-14">Brand:</span>
              <span className="font-semibold" data-id="element-15">Vabysmo</span>
            </div>
            <div className="flex justify-between" data-id="element-16">
              <span className="text-gray-400" data-id="element-17">Snak™ Type:</span>
              <span className="font-semibold" data-id="element-18">Trailer (Medium — 1–3 min)</span>
            </div>
            <div className="flex justify-between" data-id="element-19">
              <span className="text-gray-400" data-id="element-20">Canvas:</span>
              <span className="font-semibold" data-id="element-21">
                Vertical (9:16) — Mobile-first
              </span>
            </div>
            <div className="flex justify-between" data-id="element-22">
              <span className="text-gray-400" data-id="element-23">Version:</span>
              <span className="font-semibold" data-id="element-24">0.1</span>
            </div>
            <div className="flex justify-between" data-id="element-25">
              <span className="text-gray-400" data-id="element-26">Date:</span>
              <span className="font-semibold" data-id="element-27">February 15, 2026</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Cards */}
      <div className="max-w-md mx-auto px-6 py-8 space-y-6" data-id="element-28">
        {/* Campaign Overview */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-29">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-30">
            Campaign Overview
          </h2>
          <div className="space-y-3 text-sm" data-id="element-31">
            <div data-id="element-32">
              <span className="font-semibold text-gray-700" data-id="element-33">Objective:</span>
              <span className="text-gray-600 ml-2" data-id="element-34">
                Awareness and reach · Education and understanding · Lead
                generation
              </span>
            </div>
            <div data-id="element-35">
              <span className="font-semibold text-gray-700" data-id="element-36">
                Target Audience:
              </span>
              <span className="text-gray-600 ml-2" data-id="element-37">
                HCPs (Healthcare Professionals — Ophthalmologists)
              </span>
            </div>
            <div data-id="element-38">
              <span className="font-semibold text-gray-700" data-id="element-39">
                Delivery Channels:
              </span>
              <span className="text-gray-600 ml-2" data-id="element-40">
                Email, QR Code, Social Media, Website Embed
              </span>
            </div>
            <div data-id="element-41">
              <span className="font-semibold text-gray-700" data-id="element-42">
                Go-Live Target:
              </span>
              <span className="text-gray-600 ml-2" data-id="element-43">Before end of Q1 2026</span>
            </div>
            <div data-id="element-44">
              <span className="font-semibold text-gray-700" data-id="element-45">Contact:</span>
              <span className="text-gray-600 ml-2" data-id="element-46">
                Arnaud ADJA-GNAMIEN (arnaud.adja-gnamien@roche.com)
              </span>
            </div>
          </div>
        </motion.div>

        {/* Review Checklist */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-47">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-48">
            What We Need You to Review:
          </h2>
          <div className="space-y-3" data-id="element-49">
            {checklistItems.map((item, index) => <div key={index} className="flex items-start gap-3" data-id="element-50">
                <CheckSquareIcon className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-51" />
                <span className="text-sm text-gray-700" data-id="element-52">{item}</span>
              </div>)}
          </div>
        </motion.div>

        {/* Strategic Summary */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-53">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-54">
            Strategic Summary
          </h2>
          <div className="space-y-3 text-sm" data-id="element-55">
            <div data-id="element-56">
              <span className="font-semibold text-gray-700" data-id="element-57">AUT Barrier:</span>
              <span className="text-gray-600 ml-2" data-id="element-58">
                Awareness → Understanding → Trust (all gaps identified)
              </span>
            </div>
            <div data-id="element-59">
              <span className="font-semibold text-gray-700" data-id="element-60">
                FABP Backbone:
              </span>
              <span className="text-gray-600 ml-2" data-id="element-61">
                Mechanism Differentiation — "Vabysmo is the first and only
                bispecific antibody"
              </span>
            </div>
            <div data-id="element-62">
              <span className="font-semibold text-gray-700" data-id="element-63">
                Narrative Arc:
              </span>
              <span className="text-gray-600 ml-2" data-id="element-64">
                Problem (same-mechanism landscape) → Hero (Vabysmo bispecific) →
                Proof (clinical data + peer adoption) → Action
                (booking/consultation)
              </span>
            </div>
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.button initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} onClick={onBegin} className="w-full py-4 bg-[#2ec4b6] text-white font-bold text-lg rounded-lg shadow-md hover:bg-[#26a89c] transition-colors" data-id="element-65">
          Begin Journey Review →
        </motion.button>
      </div>
    </section>;
}